import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import {
  BarChart, AreaChart, Sparkline, HBarList, Donut,
  formatINR, formatINRCompact, formatNumber,
} from '../admincomponents/DashboardCharts';
import './AdminDashboard.css';

const RANGES = [
  { key: 'month', label: 'This month' },
  { key: '3m', label: 'Last 90 days' },
  { key: 'year', label: 'This year' },
];

const QUICK_ACTIONS = [
  { to: '/admin-add-student', icon: 'ph-user-plus', label: 'Add student' },
  { to: '/admin-add-batch', icon: 'ph-users-three', label: 'Add batch' },
  { to: '/admin-add-course', icon: 'ph-graduation-cap', label: 'Add course' },
  { to: '/admin-assignment-form', icon: 'ph-file-text', label: 'New assignment' },
  { to: '/admin-certificate-form', icon: 'ph-certificate', label: 'Issue certificate' },
  { to: '/admin-fees-manager', icon: 'ph-coins', label: 'Fees manager' },
  { to: '/admin-mock-exams', icon: 'ph-exam', label: 'Mock exams' },
  { to: '/admin-attendance-report', icon: 'ph-calendar-check', label: 'Attendance report' },
];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const timeAgo = (value) => {
  const t = new Date(value).getTime();
  if (!t) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

// % change vs the previous period; null when there is nothing to compare against.
const delta = (value, previous) => {
  if (!previous) return value > 0 ? { text: 'New', tone: 'up' } : null;
  const pct = Math.round(((value - previous) / previous) * 100);
  if (pct === 0) return { text: '0%', tone: 'flat' };
  return { text: `${pct > 0 ? '+' : ''}${pct}%`, tone: pct > 0 ? 'up' : 'down' };
};

function KpiCard({ icon, tone, label, value, title, sub, change, spark, children }) {
  return (
    <div className={`dash-kpi dash-tone-${tone}`}>
      <div className="dash-kpi-top">
        <span className="dash-kpi-icon"><i className={`ph ${icon}`} /></span>
        <span className="dash-kpi-label">{label}</span>
        {change && <span className={`dash-chip dash-chip-${change.tone}`}>{change.tone === 'up' ? '▲' : change.tone === 'down' ? '▼' : '•'} {change.text}</span>}
      </div>
      <div className="dash-kpi-main">
        <div>
          <div className="dash-kpi-value" title={title}>{value}</div>
          {sub && <div className="dash-kpi-sub">{sub}</div>}
        </div>
        {spark && <Sparkline values={spark} color="var(--dash-tone)" />}
      </div>
      {children}
    </div>
  );
}

function Panel({ title, subtitle, action, children, className = '' }) {
  return (
    <section className={`dash-panel ${className}`}>
      <header className="dash-panel-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

const ACTIVITY_META = {
  enrollment: { icon: 'ph-user-plus', tone: 'teal' },
  payment: { icon: 'ph-currency-inr', tone: 'green' },
  submission: { icon: 'ph-file-arrow-up', tone: 'blue' },
  certificate: { icon: 'ph-certificate', tone: 'gold' },
  exam: { icon: 'ph-exam', tone: 'purple' },
};

function activityText(a) {
  switch (a.type) {
    case 'enrollment': return <><strong>{a.name}</strong> enrolled in {a.detail}</>;
    case 'payment': return <><strong>{a.name}</strong> paid {formatINR(a.amount)}{a.detail ? ` via ${a.detail}` : ''}</>;
    case 'submission': return <><strong>{a.name}</strong> submitted “{a.detail}”</>;
    case 'certificate': return <>Certificate {a.detail} issued to <strong>{a.name}</strong></>;
    case 'exam': return <><strong>{a.name}</strong> scored {Math.round(a.percentage)}% in {a.detail} {a.result === 'pass' ? <span className="dash-pill dash-pill-ok">Passed</span> : <span className="dash-pill dash-pill-bad">Failed</span>}</>;
    default: return a.name;
  }
}

function SkeletonDashboard() {
  return (
    <div className="dash-skeleton" aria-busy="true" aria-label="Loading dashboard">
      <div className="dash-kpi-grid">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="dash-sk dash-sk-kpi" />)}</div>
      <div className="dash-grid-2"><div className="dash-sk dash-sk-chart" /><div className="dash-sk dash-sk-chart" /></div>
    </div>
  );
}

function AdminDashboard() {
  const [range, setRange] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (r) => {
    setLoading(true);
    setError('');
    try {
      const session = JSON.parse(sessionStorage.getItem('adminSession')) || {};
      if (!session.token) {
        setError('You are not logged in. Please log in again.');
        return;
      }
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/dashboard-analytics`, {
        params: { range: r, nocache: Date.now() },
        headers: { Authorization: `Bearer ${session.token}` },
      });
      if (res.data.success) setData(res.data);
      else setError(res.data.message || 'Could not load the dashboard.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const k = data?.kpis;
  const periodWord = range === 'month' ? 'last month' : range === '3m' ? 'previous 90 days' : 'last year';
  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="dash">
            {/* Header */}
            <div className="dash-header">
              <div>
                <h2>{greeting()}, Administrator <img src="/assets/images/icons/wave-hand.png" alt="" /></h2>
                <p>{todayLabel} · Here is how IFM Edu is doing</p>
              </div>
              <div className="dash-header-actions">
                <div className="dash-range" role="tablist" aria-label="Time range">
                  {RANGES.map((r) => (
                    <button key={r.key} type="button" role="tab" aria-selected={range === r.key} className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                      {r.label}
                    </button>
                  ))}
                </div>
                <button type="button" className="dash-refresh" onClick={() => load(range)} disabled={loading} title="Refresh">
                  <i className={`ph ph-arrows-clockwise ${loading ? 'dash-spin' : ''}`} />
                </button>
              </div>
            </div>

            {error && (
              <div className="dash-error">
                <span>{error}</span>
                <button type="button" onClick={() => load(range)}>Try again</button>
              </div>
            )}

            {!data && loading && <SkeletonDashboard />}

            {data && (
              <div className={loading ? 'dash-content dash-refreshing' : 'dash-content'}>
                {/* KPI cards */}
                <div className="dash-kpi-grid">
                  <KpiCard
                    icon="ph-users-three" tone="teal" label="Active students"
                    value={formatNumber(k.activeStudents.value)}
                    sub={`${formatNumber(k.activeStudents.hidden)} hidden or disabled not counted`}
                  />
                  <KpiCard
                    icon="ph-user-plus" tone="blue" label="New students"
                    value={formatNumber(k.newStudents.value)}
                    change={delta(k.newStudents.value, k.newStudents.previous)}
                    sub={`vs ${formatNumber(k.newStudents.previous)} ${periodWord}`}
                    spark={k.newStudents.spark}
                  />
                  <KpiCard
                    icon="ph-currency-inr" tone="green" label="Revenue collected"
                    value={formatINRCompact(k.revenue.value)} title={formatINR(k.revenue.value)}
                    change={delta(k.revenue.value, k.revenue.previous)}
                    sub={`${formatINRCompact(k.revenue.totalCollected)} collected all-time`}
                    spark={k.revenue.spark}
                  />
                  <KpiCard
                    icon="ph-hourglass-medium" tone="orange" label="Fees pending"
                    value={formatINRCompact(k.feesPending.value)} title={formatINR(k.feesPending.value)}
                    sub={`${formatNumber(k.feesPending.students)} student${k.feesPending.students === 1 ? '' : 's'}${k.feesPending.collectionRate != null ? ` · ${k.feesPending.collectionRate}% collected` : ''}`}
                  >
                    {k.feesPending.hiddenPending > 0 && (
                      <div className="dash-kpi-note" title="Owed by hidden or disabled students. Shown separately so it is not lost.">
                        + {formatINRCompact(k.feesPending.hiddenPending)} owed by hidden students
                      </div>
                    )}
                  </KpiCard>
                  <KpiCard
                    icon="ph-chalkboard-teacher" tone="purple" label="Active batches"
                    value={formatNumber(k.activeBatches.value)}
                    sub={`had a lesson in 30 days · ${formatNumber(k.activeBatches.total)} batches in total`}
                  >
                    {k.activeBatches.rolling > 0 && <div className="dash-kpi-note dash-kpi-note-plain">{k.activeBatches.rolling} rolling batches</div>}
                  </KpiCard>
                  <KpiCard
                    icon="ph-calendar-check" tone="teal" label="Attendance today"
                    value={formatNumber(k.attendance.today)}
                    sub={`${formatNumber(k.attendance.week)} different students this week`}
                  />
                  <KpiCard
                    icon="ph-exam" tone="gold" label="Mock exams taken"
                    value={formatNumber(k.mockExams.value)}
                    change={delta(k.mockExams.value, k.mockExams.previous)}
                    sub={k.mockExams.passRate != null ? `${k.mockExams.passRate}% pass rate` : 'no exams in this period'}
                  />
                  <KpiCard
                    icon="ph-certificate" tone="blue" label="Certificates issued"
                    value={formatNumber(k.certificates.value)}
                    change={delta(k.certificates.value, k.certificates.previous)}
                    sub={`${formatNumber(k.certificates.total)} issued in total`}
                  />
                </div>

                {/* Enrolments + courses */}
                <div className="dash-grid-2">
                  <Panel title="Course enrolments" subtitle="New enrolments per month, last 12 months">
                    <BarChart data={data.charts.enrolments} valueLabel="enrolments" />
                  </Panel>
                  <Panel title="Students by course" subtitle="Active students, top courses">
                    <HBarList items={data.charts.studentsByCourse} />
                  </Panel>
                </div>

                {/* Revenue + payment methods */}
                <div className="dash-grid-2">
                  <Panel title="Revenue collected" subtitle="Payments received per month, last 12 months">
                    <AreaChart data={data.charts.revenue} formatValue={formatINR} color="#1f9d6b" />
                  </Panel>
                  <Panel title="Payment methods" subtitle={`Collected · ${data.period.label}`}>
                    <Donut items={data.charts.paymentModes} />
                  </Panel>
                </div>

                {/* Needs attention */}
                <div className="dash-section-title">Needs your attention</div>
                <div className="dash-grid-3">
                  <Panel
                    title="Assignments to review" subtitle={`${data.attention.assignments.count} waiting for marks`}
                    action={<Link to="/admin-assignments" className="dash-link">View all</Link>}
                  >
                    {data.attention.assignments.items.length === 0 ? (
                      <div className="dash-empty dash-empty-good"><i className="ph ph-check-circle" /> All caught up</div>
                    ) : (
                      <ul className="dash-list">
                        {data.attention.assignments.items.map((a) => (
                          <li key={a.id}>
                            <Link to={`/admin-assignment-submissions/${a.assignmentId}`} className="dash-row">
                              <span className="dash-avatar">{initials(a.student)}</span>
                              <span className="dash-row-main"><strong>{a.student}</strong><em>{a.title}</em></span>
                              <span className="dash-row-meta">{timeAgo(a.submittedAt)}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>

                  <Panel
                    title="Highest pending fees" subtitle={`${formatINR(data.attention.pendingFees.total)} across ${data.attention.pendingFees.students} students`}
                    action={<Link to="/admin-fees-manager" className="dash-link">Fees manager</Link>}
                  >
                    {data.attention.pendingFees.items.length === 0 ? (
                      <div className="dash-empty dash-empty-good"><i className="ph ph-check-circle" /> No pending fees</div>
                    ) : (
                      <ul className="dash-list">
                        {data.attention.pendingFees.items.map((s) => (
                          <li key={s.id}>
                            <Link to="/admin-fees-manager" className="dash-row">
                              <span className="dash-avatar dash-avatar-orange">{initials(s.name)}</span>
                              <span className="dash-row-main"><strong>{s.name}</strong><em>{s.code || '—'} · {s.courses} course{s.courses === 1 ? '' : 's'}</em></span>
                              <span className="dash-row-amount">{formatINRCompact(s.pending)}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>

                  <Panel
                    title="Lessons needed" subtitle="Batches waiting on new lessons"
                    action={<Link to="/admin-view-batches" className="dash-link">All batches</Link>}
                  >
                    {data.attention.lessons.waiting.length === 0 && data.attention.lessons.quiet.length === 0 ? (
                      <div className="dash-empty dash-empty-good"><i className="ph ph-check-circle" /> Every batch is up to date</div>
                    ) : (
                      <ul className="dash-list">
                        {data.attention.lessons.waiting.map((b) => (
                          <li key={`w${b.batchId}`}>
                            <Link to={`/admin-batch-students/${b.batchId}`} className="dash-row">
                              <span className="dash-avatar dash-avatar-purple"><i className="ph ph-hourglass" /></span>
                              <span className="dash-row-main"><strong>{b.batch}</strong><em>{b.waiting} of {b.students} student{b.students === 1 ? '' : 's'} see{b.waiting === 1 ? 's' : ''} no lessons yet</em></span>
                              <span className="dash-pill dash-pill-warn">Rolling</span>
                            </Link>
                          </li>
                        ))}
                        {data.attention.lessons.quiet.map((b) => (
                          <li key={`q${b.batchId}`}>
                            <Link to={`/admin-view-lessons/${b.batchId}`} className="dash-row">
                              <span className="dash-avatar dash-avatar-gray"><i className="ph ph-moon" /></span>
                              <span className="dash-row-main"><strong>{b.batch}</strong><em>{b.students} student{b.students === 1 ? '' : 's'} · no new lesson</em></span>
                              <span className="dash-row-meta">{b.daysSince} days</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>
                </div>

                {/* Activity + quick actions */}
                <div className="dash-grid-2 dash-grid-activity">
                  <Panel title="Recent activity" subtitle="Latest enrolments, payments, submissions and results">
                    {data.recentActivity.length === 0 ? (
                      <div className="dash-empty">Nothing yet</div>
                    ) : (
                      <ul className="dash-activity">
                        {data.recentActivity.map((a, i) => {
                          const meta = ACTIVITY_META[a.type] || ACTIVITY_META.enrollment;
                          return (
                            <li key={`${a.type}-${i}-${a.ts}`}>
                              <span className={`dash-act-icon dash-tone-${meta.tone}`}><i className={`ph ${meta.icon}`} /></span>
                              <span className="dash-act-text">{activityText(a)}</span>
                              <span className="dash-act-time">{timeAgo(a.ts)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </Panel>
                  <Panel title="Quick actions" subtitle="Jump straight to common tasks">
                    <div className="dash-actions">
                      {QUICK_ACTIONS.map((a) => (
                        <Link key={a.to} to={a.to} className="dash-action">
                          <i className={`ph ${a.icon}`} />
                          <span>{a.label}</span>
                        </Link>
                      ))}
                    </div>
                  </Panel>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminDashboard;
